import { useState, useRef, type ChangeEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  useOrganization,
  useDeleteOrganization,
  useRemoveOrgContact,
  useUpdateOrgContact,
  useUpdateOrganization,
  useAssignOrgRole,
  useSetOrgLogo,
  useDeleteOrgLogo,
} from '../api/queries/organizations';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { PhoneInput } from '../components/PhoneInput';
import { AddressFields } from '../components/AddressFields';
import { WebsiteInput } from '../components/WebsiteInput';
import { normalizeWebsite } from '../utils/website';
import { OrgAvatar } from '../components/OrgAvatar';
import { PhotoCropModal } from '../components/PhotoCropModal';
import {
  ClientRoleEditor, VendorRoleEditor, ContractorRoleEditor, AssignRolesSection,
} from '../components/RoleEditors';
import { AddOrgContactModal } from '../components/AddOrgContactModal';
import { addressValueFromColumns, addressValueToColumns, emptyAddressValue, type AddressValue } from '../utils/address';
import { stripPhoneForStorage, formatPhoneDisplay, formatPhoneAsTyped } from '../utils/phone';
import { isAllowedPhotoMime, type OrganizationContact, type OrganizationStatus, type RoleBadge } from '@stfors/shared-types';
import { LookupSelect } from '../components/LookupSelect';
import { IconAction } from '../components/IconAction';
import { useLookupLabel } from '../api/queries/lookups';
import './DirectoryPage.css';

// Organization status is admin-managed under Settings → Lookups ('organization_status').

function MetaItem({ label, value }: { label: string; value: string | null }): JSX.Element | null {
  if (!value) return null;
  return (
    <div>
      <div className="org-meta-label">{label}</div>
      <div className="org-meta-value">{value}</div>
    </div>
  );
}

export function OrganizationDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const params = useParams();
  const id = Number(params['id']);
  const { data, isPending, error } = useOrganization(Number.isInteger(id) ? id : null);
  const removeOrg = useDeleteOrganization();
  const removeContact = useRemoveOrgContact(id);
  const updateContact = useUpdateOrgContact(id);
  const updateOrg = useUpdateOrganization();
  const assignRole = useAssignOrgRole();
  const setLogo = useSetOrgLogo(id);
  const deleteLogo = useDeleteOrgLogo(id);

  const orgStatusLabel = useLookupLabel('organization_status');
  const roleLabel = useLookupLabel('contact_role');

  // Org-fields edit mode.
  const [editing, setEditing] = useState(false);
  const [eName, setEName] = useState('');
  const [eDba, setEDba] = useState('');
  const [eEmail, setEEmail] = useState('');
  const [ePhone, setEPhone] = useState('');
  const [eWebsite, setEWebsite] = useState('');
  const [eEin, setEEin] = useState('');
  const [eIndustry, setEIndustry] = useState('');
  const [eStatus, setEStatus] = useState<OrganizationStatus>('active');
  const [eAddr, setEAddr] = useState<AddressValue>(emptyAddressValue());
  const [eNotes, setENotes] = useState('');
  const [editError, setEditError] = useState<string | null>(null);

  const [showAdd, setShowAdd] = useState(false);

  // Company logo editor — pick → crop/zoom → upload immediately. `logoVersion`
  // bumps the <OrgAvatar> refreshKey after a replace (where has_logo stays true).
  const [pendingLogo, setPendingLogo] = useState<File | null>(null);
  const [logoVersion, setLogoVersion] = useState(0);
  const [logoErr, setLogoErr] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  if (isPending) return <p className="directory-status">Loading company…</p>;
  if (error || !data) {
    return (
      <p className="directory-status directory-error">
        {error instanceof ApiError && error.status === 404 ? 'Company not found.' : 'Failed to load the company.'}
      </p>
    );
  }

  const org = data.organization;
  const roleLink = (r: RoleBadge): string | null => {
    if (r === 'client' && org.client_id) return `/clients/${org.client_id}`;
    if (r === 'contractor') return '/contractors';
    return null;
  };

  const onDelete = async () => {
    const label = org.dba_name?.trim() || org.name;
    if (!window.confirm(`Delete ${label}? This can't be undone.`)) return;
    try {
      await removeOrg.mutateAsync(org.id);
      navigate('/directory');
    } catch (err) {
      window.alert(err instanceof ApiError ? err.message : 'Could not delete the company.');
    }
  };

  const startEditOrg = () => {
    setEName(org.name);
    setEDba(org.dba_name ?? '');
    setEEmail(org.email ?? '');
    setEPhone(org.phone ? formatPhoneAsTyped(org.phone) : '');
    setEWebsite(org.website ?? '');
    setEEin(org.ein ?? '');
    setEIndustry(org.industry ?? '');
    setEStatus(org.status);
    setEAddr(addressValueFromColumns(org));
    setENotes(org.notes ?? '');
    setEditError(null);
    setEditing(true);
  };

  const onSaveOrg = async () => {
    setEditError(null);
    if (eName.trim().length === 0) { setEditError('Company name is required.'); return; }
    try {
      await updateOrg.mutateAsync({
        id,
        name: eName.trim(),
        dba_name: eDba.trim() || null,
        email: eEmail.trim() || null,
        phone: stripPhoneForStorage(ePhone) || null,
        website: normalizeWebsite(eWebsite) || null,
        ein: eEin.trim() || null,
        industry: eIndustry.trim() || null,
        status: eStatus,
        ...(() => {
          const cols = addressValueToColumns(eAddr);
          return {
            address_line1: cols.line1, address_line2: cols.line2, city: cols.city,
            state: cols.state, postal_code: cols.postal_code, country: cols.country ?? undefined,
            // Clear the legacy free-text blob once structured fields are saved.
            address: null,
          };
        })(),
        notes: eNotes.trim() || null,
      });
      setEditing(false);
    } catch (err) {
      setEditError(err instanceof ApiError ? err.message : 'Could not save the company.');
    }
  };

  const onPickLogo = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    e.target.value = ''; // let the same file be re-picked
    if (!f) return;
    setLogoErr(null);
    if (!isAllowedPhotoMime(f.type)) {
      setLogoErr('Choose a JPEG, PNG, GIF, or WebP image.');
      return;
    }
    setPendingLogo(f);
  };

  const onCropLogoConfirm = async (cropped: File) => {
    try {
      await setLogo.mutateAsync(cropped);
      setLogoVersion((v) => v + 1);
      setPendingLogo(null);
    } catch (err) {
      setLogoErr(err instanceof ApiError ? err.message : 'Could not save the logo. Try again.');
      setPendingLogo(null);
    }
  };

  const onRemoveLogo = async () => {
    if (!window.confirm('Remove this company logo?')) return;
    setLogoErr(null);
    try {
      await deleteLogo.mutateAsync();
      setLogoVersion((v) => v + 1);
    } catch (err) {
      setLogoErr(err instanceof ApiError ? err.message : 'Could not remove the logo. Try again.');
    }
  };

  const addressParts = [org.address, org.address_line1, org.address_line2,
    [org.city, org.state, org.postal_code].filter(Boolean).join(', ')]
    .filter((s) => s && s.length > 0);

  return (
    <section className="section-shell">
      <header className="section-header">
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
          <div className="pdp-avatar-block">
            <OrgAvatar
              orgId={org.id}
              hasLogo={org.has_logo}
              name={org.dba_name?.trim() || org.name}
              size={56}
              refreshKey={logoVersion}
            />
            {can('organization.write') && (
              <div className="pdp-photo-actions">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  hidden
                  onChange={onPickLogo}
                />
                <button
                  type="button"
                  className="btn-text pdp-photo-btn"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={setLogo.isPending || deleteLogo.isPending}
                >
                  {org.has_logo ? 'Change' : 'Add logo'}
                </button>
                {org.has_logo && (
                  <button
                    type="button"
                    className="btn-text pdp-photo-btn"
                    onClick={onRemoveLogo}
                    disabled={setLogo.isPending || deleteLogo.isPending}
                  >
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>
          <div>
            <h2>{org.dba_name?.trim() ? org.dba_name.trim() : org.name}</h2>
            {org.dba_name?.trim() && (
              <p className="org-meta-value">Legal name: {org.name}</p>
            )}
            {logoErr && (
              <p className="new-client-error" role="alert" style={{ margin: '4px 0 0' }}>{logoErr}</p>
            )}
            <div className="directory-roles" style={{ marginTop: 6 }}>
              <span className="entity-chip entity-chip-company">Company</span>
              {org.roles.map((r) => {
                const href = roleLink(r);
                const label = roleLabel(r) || r;
                return href ? (
                  <Link key={r} to={href} className={`role-badge role-${r}`}>{label}</Link>
                ) : (
                  <span key={r} className={`role-badge role-${r}`}>{label}</span>
                );
              })}
            </div>
          </div>
        </div>
        <div className="directory-header-actions">
          {can('organization.write') && !editing && (
            <IconAction action="edit" onClick={startEditOrg} />
          )}
          <button type="button" className="btn-text" onClick={() => navigate('/directory')}>
            ← Back to directory
          </button>
        </div>
      </header>

      <div className="section-body">
        {editing ? (
          <div className="new-client-form" style={{ padding: 0 }}>
            <div className="new-client-row">
              <label className="new-client-field"><span>Company name *</span>
                <input className="form-input" value={eName} onChange={(e) => setEName(e.target.value)} maxLength={200} />
              </label>
              <label className="new-client-field"><span>Doing business as</span>
                <input className="form-input" value={eDba} onChange={(e) => setEDba(e.target.value)} maxLength={200} />
              </label>
            </div>
            <div className="new-client-row">
              <label className="new-client-field"><span>Email</span>
                <input className="form-input" type="email" value={eEmail} onChange={(e) => setEEmail(e.target.value)} maxLength={254} />
              </label>
              <label className="new-client-field"><span>Phone</span>
                <PhoneInput className="form-input" value={ePhone} onChange={setEPhone} />
              </label>
            </div>
            <div className="new-client-row">
              <label className="new-client-field"><span>Website</span>
                <WebsiteInput value={eWebsite} onChange={setEWebsite} disabled={updateOrg.isPending} />
              </label>
              <label className="new-client-field"><span>EIN</span>
                <input className="form-input" value={eEin} onChange={(e) => setEEin(e.target.value)} placeholder="XX-XXXXXXX" maxLength={20} />
              </label>
            </div>
            <div className="new-client-row">
              <label className="new-client-field"><span>Industry</span>
                <input className="form-input" value={eIndustry} onChange={(e) => setEIndustry(e.target.value)} maxLength={100} />
              </label>
              <label className="new-client-field"><span>Status</span>
                <LookupSelect category="organization_status" className="form-input" value={eStatus} onChange={setEStatus} aria-label="Status" />
              </label>
            </div>
            <div className="new-client-field new-client-field-full"><span>Address</span>
              <AddressFields value={eAddr} onChange={setEAddr} legacyText={org.address} autoParse disabled={updateOrg.isPending} />
            </div>
            <label className="new-client-field new-client-field-full"><span>Notes</span>
              <textarea className="form-input new-client-textarea" rows={2} value={eNotes} onChange={(e) => setENotes(e.target.value)} maxLength={10_000} />
            </label>
            {editError && <p className="new-client-error" role="alert">{editError}</p>}
            <div className="new-client-actions">
              <IconAction
                action="cancel"
                onClick={() => setEditing(false)}
                disabled={updateOrg.isPending}
              />
              <IconAction
                action="save"
                onClick={onSaveOrg}
                disabled={updateOrg.isPending}
                label={updateOrg.isPending ? 'Saving…' : 'Save'}
              />
            </div>
          </div>
        ) : (
          <div className="org-detail-meta">
            <MetaItem label="Email" value={org.email} />
            <MetaItem label="Phone" value={org.phone ? formatPhoneDisplay(org.phone) : null} />
            <MetaItem label="Website" value={org.website} />
            <MetaItem label="EIN" value={org.ein ? `${org.ein.slice(0, 2)}-${org.ein.slice(2)}` : null} />
            <MetaItem label="Industry" value={org.industry} />
            <MetaItem label="Address" value={addressParts.length > 0 ? addressParts.join(' · ') : null} />
            <MetaItem label="Status" value={orgStatusLabel(org.status)} />
            {org.notes && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div className="org-meta-label">Notes</div>
                <div className="org-meta-value" style={{ whiteSpace: 'pre-wrap' }}>{org.notes}</div>
              </div>
            )}
          </div>
        )}

        {org.client_id !== null && <ClientRoleEditor clientId={org.client_id} />}
        {org.vendor_id !== null && <VendorRoleEditor vendorId={org.vendor_id} />}
        {org.contractor_id !== null && <ContractorRoleEditor contractorId={org.contractor_id} />}

        <AssignRolesSection
          scope="company"
          heldRoles={org.roles}
          defaultEmail={org.email ?? ''}
          busy={assignRole.isPending}
          onAssign={async (role, opts) => {
            await assignRole.mutateAsync({ orgId: id, role, email: opts?.email });
          }}
        />

        <div className="org-section-header" style={{ marginTop: 'var(--space-6)' }}>
          <h3>Contacts</h3>
          {can('organization.write') && (
            <button type="button" className="btn-cyan-primary" onClick={() => setShowAdd(true)}>
              Add contact
            </button>
          )}
        </div>

        {org.contacts.length === 0 ? (
          <p className="org-meta-label">No contacts linked yet.</p>
        ) : (
          <ul className="org-contacts-list">
            {org.contacts.map((c: OrganizationContact) => (
              <li key={c.id} className="org-contact-row">
                <div>
                  {c.person_id ? (
                    <Link to={`/people/${c.person_id}`} className="org-contact-name">
                      {c.display_name ?? c.person?.display_name ?? 'Contact'}
                    </Link>
                  ) : (
                    <span className="org-contact-name">{c.display_name ?? c.person?.display_name ?? 'Contact'}</span>
                  )}
                  {c.is_primary && <span className="org-contact-primary">Primary</span>}
                  {c.title && <div className="org-contact-meta">{c.title}</div>}
                </div>
                {can('organization.write') && (
                  <div className="org-contact-actions">
                    {!c.is_primary && (
                      <button
                        type="button"
                        className="btn-text"
                        onClick={() => updateContact.mutate({ contactId: c.id, is_primary: true })}
                        disabled={updateContact.isPending}
                        title="Make this the primary contact"
                      >
                        Make primary
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-text"
                      onClick={() => removeContact.mutate(c.id)}
                      disabled={removeContact.isPending}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {can('organization.write') && (
          <div className="org-section-header">
            <button type="button" className="btn-charcoal-secondary" onClick={onDelete} disabled={removeOrg.isPending}>
              Delete company
            </button>
          </div>
        )}
      </div>

      <AddOrgContactModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        orgId={id}
        orgName={org.name}
        existingPersonIds={org.contacts.map((c) => c.person_id).filter((pid): pid is number => pid != null)}
      />

      {pendingLogo && (
        <PhotoCropModal
          file={pendingLogo}
          open
          busy={setLogo.isPending}
          onCancel={() => { if (!setLogo.isPending) setPendingLogo(null); }}
          onConfirm={onCropLogoConfirm}
        />
      )}
    </section>
  );
}
